import { RedoOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Card, ConfigProvider, Form, Input, Select, Space, Spin } from "antd"
import type React from "react"
import { memo, useState, useEffect, useMemo } from "react"
import type { SetSearchParams } from "@/services/dictionary/setApi"
import { useQuery } from "@tanstack/react-query"
import { userService } from "@/services/user/userApi"
import { useDebounce } from "ahooks"

interface SetSearchFormProps {
  onSearch: (values: Omit<SetSearchParams, "page" | "per_page">) => void
  loading?: boolean
  initialValues?: Omit<SetSearchParams, "page" | "per_page">
}

const SetSearchForm: React.FC<SetSearchFormProps> = memo(({ onSearch, loading = false, initialValues }) => {
  const [form] = Form.useForm()
  const [userSearch, setUserSearch] = useState("")
  const debouncedUserSearch = useDebounce(userSearch, { wait: 300 })

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues)
    }
  }, [initialValues, form])

  const { data: usersResult, isFetching: loadingUsers } = useQuery({
    queryKey: ["users_search", debouncedUserSearch],
    queryFn: () => userService.list({ page: 1, limit: 50, search: debouncedUserSearch || undefined }),
  })

  const handleSearch = () => {
    form.validateFields().then((values) => {
      onSearch(values)
    })
  }

  const handleReset = () => {
    form.resetFields()
    setUserSearch("")
    onSearch({
      search: "",
      visibility: undefined,
      user_id: undefined,
      has_description: undefined,
    })
  }

  const visibilityOptions = [
    { label: "Công khai (public)", value: "public" },
    { label: "Riêng tư (private)", value: "private" },
  ]

  const hasDescriptionOptions = [
    { label: "Có mô tả", value: "yes" },
    { label: "Không có mô tả", value: "no" },
  ]

  const userOptions = useMemo(() => {
    const options =
      usersResult?.data?.map((user: any) => ({
        label: `${user.username || user.name} (${user.email})`,
        value: user.id,
      })) || []

    const initialUserId = initialValues?.["user_id"]
    if (initialUserId && !options.some((o: any) => o.value === initialUserId)) {
      return [{ label: `User ID: ${initialUserId}`, value: initialUserId }, ...options]
    }
    return options
  }, [usersResult, initialValues])

  return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 0,
          },
        },
      }}
    >
      <Card>
        <Form
          form={form}
          initialValues={{
            search: "",
            visibility: undefined,
            user_id: undefined,
            has_description: undefined,
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5"
        >
          <Form.Item name="search" label="Tìm kiếm" colon={false} className="mb-0">
            <Input allowClear autoComplete="off" placeholder="Nhập tên bộ từ..." onPressEnter={handleSearch} />
          </Form.Item>

          <Form.Item name="user_id" label="Người tạo" colon={false} className="mb-0">
            <Select
              allowClear
              showSearch
              placeholder="Chọn người tạo"
              filterOption={false}
              onSearch={setUserSearch}
              loading={loadingUsers}
              notFoundContent={loadingUsers ? <Spin size="small" /> : null}
              options={userOptions}
              onChange={handleSearch}
            />
          </Form.Item>

          <Form.Item name="visibility" label="Hiển thị" colon={false} className="mb-0">
            <Select allowClear placeholder="Chọn chế độ" options={visibilityOptions} onChange={handleSearch} />
          </Form.Item>

          <Form.Item name="has_description" label="Mô tả" colon={false} className="mb-0">
            <Select allowClear placeholder="Chọn trạng thái" options={hasDescriptionOptions} onChange={handleSearch} />
          </Form.Item>

          <Form.Item label=" " colon={false} className="mb-0">
            <Space>
              <Button type="default" icon={<RedoOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Tìm kiếm
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </ConfigProvider>
  )
})

SetSearchForm.displayName = "SetSearchForm"

export default SetSearchForm
